// Resilient persistence for post-interview writes (B2C, CRM, Kolleno).
//
// The final save after an interview is the one write we must never lose: the
// candidate/user already spent 30-60 min (and often a quota or a recruiter's
// pipeline slot). These saves run at the END of a long session, so the auth
// cookie is often stale (B2C) or the network/LLM flaky — a naive
// fire-and-forget fetch().catch(() => {}) silently drops the report and the UI
// shows a success screen anyway.
//
// This module makes any such save bulletproof:
//   1. (optional) mint a FRESH Clerk token and send it as a Bearer header so a
//      fresh token wins over a stale __session cookie (auth() accepts a
//      session_token from Authorization; networkless verify via publishableKey);
//   2. retry on 401 / 5xx / network with backoff;
//   3. on total failure, persist the payload to localStorage under a per-flow
//      queue key and flush it on the next relevant page mount — nothing is lost.

type GetToken = (options?: { skipCache?: boolean }) => Promise<string | null>;

export interface ResilientJob {
  url: string;
  payload: unknown;
  method?: string;
}

interface SaveOptions {
  getToken?: GetToken;
  queueKey?: string;
  delays?: number[]; // injectable for fast unit tests
}

const MAX_QUEUE = 20;
const RETRY_DELAYS = [0, 800, 2500];

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function readQueue(key: string): ResilientJob[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(key: string, items: ResilientJob[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(items.slice(-MAX_QUEUE)));
  } catch {
    // localStorage full / disabled — nothing more we can do.
  }
}

function enqueue(key: string, job: ResilientJob): void {
  writeQueue(key, [...readQueue(key), job]);
}

// One request attempt, with a freshly-minted Bearer token when getToken is given.
async function postOnce(job: ResilientJob, getToken?: GetToken): Promise<Response> {
  let token: string | null = null;
  if (getToken) {
    try {
      token = await getToken({ skipCache: true });
    } catch {
      token = null;
    }
  }
  return fetch(job.url, {
    method: job.method ?? "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(job.payload),
  });
}

// Retry transient failures. `retriable` tells the caller whether a failure is
// worth queueing (401 / 5xx / network) or permanent (other 4xx — bad payload).
async function attemptPost(
  job: ResilientJob,
  getToken: GetToken | undefined,
  delays: number[],
): Promise<{ ok: boolean; retriable: boolean }> {
  for (let i = 0; i < delays.length; i++) {
    if (delays[i] > 0) await sleep(delays[i]);
    try {
      const res = await postOnce(job, getToken);
      if (res.ok) return { ok: true, retriable: false };
      if (res.status >= 400 && res.status < 500 && res.status !== 401) {
        return { ok: false, retriable: false };
      }
    } catch {
      // network error → retry
    }
  }
  return { ok: false, retriable: true };
}

/**
 * Save with retry; on total (retriable) failure, queue to localStorage for a
 * later flush. Returns true if the server accepted it, false if queued.
 */
export async function resilientSave(job: ResilientJob, opts: SaveOptions = {}): Promise<boolean> {
  const { ok, retriable } = await attemptPost(job, opts.getToken, opts.delays ?? RETRY_DELAYS);
  if (!ok && retriable && opts.queueKey) enqueue(opts.queueKey, job);
  return ok;
}

/**
 * Like resilientSave but RETURNS the Response (retries transient failures) so
 * callers that need the response body — e.g. a server-assigned id — stay
 * robust. Returns null only when every attempt threw (network down).
 */
export async function resilientFetch(
  job: ResilientJob,
  opts: { getToken?: GetToken; delays?: number[] } = {},
): Promise<Response | null> {
  const delays = opts.delays ?? RETRY_DELAYS;
  let last: Response | null = null;
  for (let i = 0; i < delays.length; i++) {
    if (delays[i] > 0) await sleep(delays[i]);
    try {
      const res = await postOnce(job, opts.getToken);
      if (res.ok) return res;
      last = res;
      // Permanent client error — retrying won't help.
      if (res.status >= 400 && res.status < 500 && res.status !== 401) return res;
    } catch {
      last = null;
    }
  }
  return last;
}

const flushing = new Set<string>();

/** Drain a flow's queue (call on mount). Survivors stay queued. */
export async function flushQueue(queueKey: string, opts: SaveOptions = {}): Promise<number> {
  if (flushing.has(queueKey)) return 0;
  const queued = readQueue(queueKey);
  if (queued.length === 0) return 0;

  flushing.add(queueKey);
  try {
    writeQueue(queueKey, []); // clear up front so a concurrent flush can't double-post
    const survivors: ResilientJob[] = [];
    let flushed = 0;
    for (const job of queued) {
      const { ok, retriable } = await attemptPost(job, opts.getToken, opts.delays ?? RETRY_DELAYS);
      if (ok) flushed++;
      else if (retriable) survivors.push(job);
      // permanent failures are dropped — re-trying a 400 forever is pointless.
    }
    if (survivors.length) writeQueue(queueKey, [...readQueue(queueKey), ...survivors]);
    return flushed;
  } finally {
    flushing.delete(queueKey);
  }
}

// ── B2C helpers (stable API; the original B2C save call sites use these) ──────

export interface InterviewSavePayload {
  job_description: string;
  competencies: unknown;
  report: unknown;
  started_at: string | null;
  completed_at: string;
  webcam_recording_url?: string | null;
}

const B2C_QUEUE_KEY = "pending_interview_saves";

export async function saveInterview(
  payload: InterviewSavePayload,
  getToken: GetToken,
  opts: { delays?: number[] } = {},
): Promise<boolean> {
  return resilientSave(
    { url: "/api/b2c/interviews", payload },
    { getToken, queueKey: B2C_QUEUE_KEY, delays: opts.delays },
  );
}

export async function flushPendingInterviewSaves(
  getToken: GetToken,
  opts: { delays?: number[] } = {},
): Promise<number> {
  return flushQueue(B2C_QUEUE_KEY, { getToken, delays: opts.delays });
}
